import { Link } from 'react-router-dom';
import type { Topic } from '@/mocks/topics';
import { formatHeat } from '@/utils/format';

interface TopicCardProps {
  topic: Topic;
  variant?: 'default' | 'compact';
}

export default function TopicCard({ topic, variant = 'default' }: TopicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/topics/${topic.id}`}
        className="group flex cursor-pointer items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
      >
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
          <i className="ri-fire-line text-lg" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600">
            {topic.title}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">{topic.category}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" />
              {formatHeat(topic.heat)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/topics/${topic.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[8/5] w-full overflow-hidden">
        <img
          src={topic.cover}
          alt={`${topic.title} - 话题封面`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-3 py-1 text-xs font-medium text-primary-700 backdrop-blur">
          {topic.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
          {topic.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {topic.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" />
            热度 {formatHeat(topic.heat)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            发酵 {topic.evolveHours} 小时
          </span>
        </div>
      </div>
    </Link>
  );
}